/* =========================================================================
 * THE GPT BRIDGE — the pure half. No network, no DOM, so every rule here is
 * asserted by verify-gpt-bridge.mjs rather than trusted.
 *
 * WHAT THE BRIDGE IS. A Custom GPT has no API: nothing on a server can call
 * one or be handed an image back by one. So the bridge is Brian's own browser,
 * signed in to his own ChatGPT, doing what he would otherwise do by hand:
 * paste the assembled prompt, attach the references, wait, save the image,
 * upload it. The SR GPT Bridge extension (tools/sr-gpt-bridge-extension/)
 * does those steps in his tab; the manual path does them with his hands. Both
 * end in the same place: the image goes through the owned-asset upload route
 * into HubSpot File Manager, and a provenance line goes onto the build record.
 *
 * WHAT IT MAY NEVER DO: call itself an owned mark. An image a model drew in a
 * chat is drawn-by-model, whatever reference it was shown. OWNED_MARK_SOURCES
 * in lib/championToClose.js has exactly one value and this module never
 * produces it; verify-gpt-bridge asserts that.
 * ========================================================================= */

export const GPT_BRIDGE = Object.freeze({
  SOURCE: 'gpt-bridge',
  MARK: 'drawn-by-model',
  KIND: 'gpt-bridge',          // File Manager folder: SR Artwork Renders/uploads/gpt-bridge
  TAG: '[gpt-bridge]',         // how a build-record history note is recognised
  ROUTE_AUTO: 'extension',
  ROUTE_MANUAL: 'manual',
})

/**
 * ⚠️ THE EXTENSION VERSION THE APP NEEDS, and why this constant exists.
 *
 * 2026-09-18: the bridge refused an owned mark with "not an SR-owned URL"
 * because the extension's allowlist allowed exactly one label in front of
 * `hubspotusercontent`, and SR's File Manager host has two. The fix is in
 * background.js — and a Netlify deploy DOES NOT SHIP IT. The extension is
 * loaded unpacked from tools/sr-gpt-bridge-extension, so the browser keeps
 * running the old copy until somebody presses reload on chrome://extensions.
 *
 * A site that silently works against a stale extension gives the operator a
 * mystery. So the app knows the version it needs, compares it to the one the
 * extension announces, and says plainly which one is running and what to do.
 */
export const BRIDGE_MIN_VERSION = '1.4.0'
export const BRIDGE_DOWNLOAD_URL = `/downloads/sr-gpt-bridge-${BRIDGE_MIN_VERSION}.zip`

/** Compare dotted versions. -1, 0, 1. Missing parts read as 0. */
export function compareVersions(a, b) {
  const pa = String(a || '').split('.').map((n) => parseInt(n, 10) || 0)
  const pb = String(b || '').split('.').map((n) => parseInt(n, 10) || 0)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) > (pb[i] || 0) ? 1 : -1
  }
  return 0
}

/** Does the running extension predate the current handoff fixes? */
export function bridgeIsStale(version) {
  return compareVersions(version, BRIDGE_MIN_VERSION) < 0
}

/** What to tell somebody whose extension is behind the site. */
export function staleBridgeText(version) {
  return `The SR GPT Bridge extension in this browser is v${version || 'unknown'}, and this build needs v${BRIDGE_MIN_VERSION}. `
    + 'v1.4.0 uses the current message box and its visible Send button, verifies every reference, and confirms that ChatGPT received the message. '
    + 'Download the update below, extract it into your existing bridge folder, then open chrome://extensions, find SR GPT Bridge and press Reload. '
    + 'Reload Artwork Studio afterwards. A site update cannot replace files in your local extension folder. '
    + 'Until then the four manual steps below still work.'
}

const clean = (v) => String(v == null ? '' : v).replace(/[|\n\r]+/g, ' ').trim()

/** "20260918T142233Z" — sortable, filename-safe. */
export function stamp(at) {
  const d = new Date(at)
  return Number.isNaN(d.getTime()) ? 'unknown-time' : d.toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z')
}

/**
 * The File Manager file name. It carries the provenance on its own, so the
 * file still says where it came from if the build-record note could not be
 * written (a role without ARTWORK_BUILD_SUBMIT, say).
 */
export function bridgeFileName({ buildId, view, at, ext = 'png' }) {
  const b = clean(buildId).replace(/[^\w-]+/g, '_') || 'nobuild'
  const v = clean(view).replace(/[^\w-]+/g, '_') || 'view'
  const e = String(ext || 'png').replace(/^\./, '').toLowerCase()
  return `${b}-${v}-gpt-bridge-${stamp(at)}.${e}`
}

/**
 * One history note, written onto the build record through the existing
 * POST /api/artwork/build/:id/status with the build's CURRENT status, so the
 * status does not move and the history gains a line. Plain key=value pairs,
 * so a person can read it and parseProvenance can too.
 */
export function provenanceNote({ view, fileId, url, route, at, gptUrl, chatUrl }) {
  const parts = [
    GPT_BRIDGE.TAG,
    `view=${clean(view)}`,
    `mark=${GPT_BRIDGE.MARK}`,
    `route=${route === GPT_BRIDGE.ROUTE_AUTO ? GPT_BRIDGE.ROUTE_AUTO : GPT_BRIDGE.ROUTE_MANUAL}`,
    `at=${new Date(at).toISOString()}`,
    fileId ? `file=${clean(fileId)}` : '',
    `url=${clean(url)}`,
    gptUrl ? `gpt=${clean(gptUrl)}` : '',
    chatUrl ? `chat=${clean(chatUrl)}` : '',
  ].filter(Boolean)
  return parts.join(' | ')
}

/** Every bridge entry in a build record's history, oldest first. */
export function parseProvenance(history) {
  const out = []
  for (const h of Array.isArray(history) ? history : []) {
    const note = String(h?.note || '')
    if (!note.startsWith(GPT_BRIDGE.TAG)) continue
    const kv = {}
    for (const part of note.slice(GPT_BRIDGE.TAG.length).split('|')) {
      const i = part.indexOf('=')
      if (i > 0) kv[part.slice(0, i).trim()] = part.slice(i + 1).trim()
    }
    if (!kv.url) continue
    out.push({
      view: kv.view || '',
      url: kv.url,
      fileId: kv.file || '',
      route: kv.route || GPT_BRIDGE.ROUTE_MANUAL,
      at: kv.at || h.at || '',
      gpt: kv.gpt || '',
      chat: kv.chat || '',
      // Whatever the note says, a bridge image is drawn by a model. A tampered
      // or future note cannot talk its way into an owned mark from here.
      mark: GPT_BRIDGE.MARK,
      by: h.by || null,
      source: GPT_BRIDGE.SOURCE,
    })
  }
  return out
}

/** The newest bridge image for one view, or null. */
export function latestBridgeImage(history, view) {
  const all = parseProvenance(history).filter((p) => !view || p.view === view)
  return all.length ? all[all.length - 1] : null
}

/** One line a person reads under the image. */
export function provenanceLine(p, { locale } = {}) {
  if (!p) return ''
  const d = new Date(p.at)
  const when = Number.isNaN(d.getTime()) ? 'at an unrecorded time'
    : d.toLocaleString(locale, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  const how = p.route === GPT_BRIDGE.ROUTE_AUTO ? 'sent automatically by the SR GPT Bridge' : 'uploaded by hand'
  return `Made in the GPT, ${how}, ${when}. Owned copy in HubSpot File Manager. The mark is drawn by the model, not the school's crest.`
}

/** Human words for each bridge step, shown while it runs. */
export const STEP_LABEL = Object.freeze({
  'starting': 'Starting',
  'fetching-references': 'Collecting the references',
  'opening': 'Opening your GPT',
  'waiting-for-chatgpt': 'Waiting for ChatGPT to load',
  'attaching': 'Attaching the references',
  'typing': 'Pasting the prompt',
  'sending': 'Sending',
  'generating': 'The GPT is drawing',
  'fetching-image': 'Collecting the image',
  'uploading': 'Uploading to HubSpot File Manager',
  'recording': 'Recording it on the build',
  'done': 'Done',
})

/** What a failure code means, and what to do next. Every one ends at the manual path. */
export function failureText(code, detail) {
  const d = detail ? ` (${String(detail).slice(0, 240)})` : ''
  switch (code) {
    case 'no_extension': return 'The SR GPT Bridge extension is not running in this browser. Use the four manual steps below; they do the same job.'
    case 'no_gpt': return 'No GPT is configured. Set one for this device below, or ask for sr-runtime-config.json to be updated.'
    case 'signed_out': return 'ChatGPT is signed out in this browser. Sign in to ChatGPT in the tab that opened, then press the button again, or use the manual steps below.'
    case 'no_composer': return `Could not find ChatGPT's message box${d}. ChatGPT may have changed its page. Use the manual steps below.`
    /* ⚠️ THE DETAIL IS THE WHOLE MESSAGE HERE, so it leads rather than sitting
     * in brackets at the end. This case fired on 2026-09-18 with the useless
     * "(job-crest.png: not an SR-owned URL)" on a file that WAS owned, and the
     * bracket made it read like noise rather than the diagnosis. */
    case 'attach_failed': return detail
      ? `The mark did not reach the GPT: ${String(detail).slice(0, 300)}`
      : 'The references did not attach, and the extension gave no reason. Use the manual steps below — they attach the same files by hand.'
    case 'prompt_not_inserted': return `The bridge could not keep the complete prompt in ChatGPT’s message box${d}. Nothing was sent. Copy the prompt below into the open ChatGPT tab.`
    case 'draft_present': return 'The ChatGPT tab already contains an unsent draft. It was kept intact. Use a fresh chat or finish that draft, then use the steps below.'
    case 'send_blocked': return `The bridge could not use ChatGPT’s Send control${d}. The prepared chat is still open. Complete the send there, then upload the returned image below.`
    case 'send_unconfirmed': return 'The bridge clicked Send but could not confirm a new message in ChatGPT. Check that tab before sending again to avoid a duplicate request. Upload the returned image below when it arrives.'
    case 'no_image': return `The GPT wrote a reply instead of drawing a ring${d}. A GPT that writes build specs will always do this; point the bridge at a GPT that generates images ("Change the GPT for this device" below), or answer it in the ChatGPT tab and upload the image by hand below.`
    case 'timeout': return `No image after several minutes${d}. The ChatGPT tab is still open; if an image arrives, save it and upload it below.`
    case 'image_unreadable': return `The image appeared but could not be copied out of ChatGPT${d}. Save it from the ChatGPT tab and upload it below.`
    case 'tab_closed': return 'The ChatGPT tab was closed before the image arrived. Use the manual steps below.'
    case 'challenge': return 'ChatGPT is asking for a human check. The bridge will not answer it for you: complete it in the ChatGPT tab, then use the manual steps below.'
    case 'upload_failed': return `The image came back but did not upload${d}. Nothing was attached; the ChatGPT tab still has it. Try the upload below.`
    case 'busy': return 'The bridge is already running a job in this browser. Wait for it to finish.'
    default: return `The bridge stopped${d}. Use the manual steps below.`
  }
}
